import { useContext, useEffect, useRef, useState } from 'react'
import { UNSAFE_NavigationContext, useLocation } from 'react-router-dom'
import { useStore } from 'zustand'
import { useTranslation } from 'react-i18next'
import { Button } from '@quebon/ui/components/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@quebon/ui/components/dialog'
import { hasHandheldDrafts, type HandheldDraftStore } from './handheldDraftStore'
import { COACHING_EXIT_EVENT } from './handheldNavigation'

export interface NavigationGuardOptions {
  hasAdditionalDrafts?: () => boolean
  /** A host with one canonical selection can keep task tabs within the same editor scope. */
  scopeKey?: (url: URL) => string
  isAdditionalSaving?: () => boolean
  discardAdditionalDrafts?: () => void
}

function lessonScope(url: URL, includeOperations: boolean) {
  const attempt = url.searchParams.get('attempt')
  const attemptScope = attempt != null && attempt !== 'progress' ? attempt
    : url.searchParams.get('review') === 'true' ? 'review' : 'progress'
  return [url.pathname, url.searchParams.get('learnerId'), url.searchParams.get('lessonId'), attemptScope,
    url.searchParams.get('testKind'), url.searchParams.get('scopeContentId'),
    url.searchParams.get('chapterOrdinal'), url.searchParams.get('unitAssessmentId'),
    url.searchParams.get('workspace') ?? 'grading',
    ...(includeOperations ? ['contentKind', 'contentId', 'contentTestKind', 'contentScopeId', 'contentChapter', 'contentAssessmentId', 'contentCurriculum', 'contentCourse', 'contentModule'].map(key => url.searchParams.get(key)) : []),
  ].join('|')
}

/** BrowserRouter has no data-router blocker. Guard its navigator and capture POP before routing. */
export function useHandheldNavigationGuard(store: HandheldDraftStore, options?: NavigationGuardOptions) {
  const { navigator } = useContext(UNSAFE_NavigationContext)
  const location = useLocation()
  const currentOptions = useRef(options)
  currentOptions.current = options
  const approvedExit = useRef(false)
  const currentLocation = useRef(location)
  currentLocation.current = location
  const [pending, setPending] = useState<(() => void) | null>(null)
  const storeSaving = useStore(store, state => state.saving)
  const saving = storeSaving || (options?.isAdditionalSaving?.() ?? false)
  const { t } = useTranslation('coaching')

  useEffect(() => {
    const originalPush = navigator.push
    const originalReplace = navigator.replace
    const originalGo = navigator.go
    let index: number | null = typeof window.history.state?.idx === 'number' ? window.history.state.idx : null
    let restoreAction: (() => void) | null = null
    let approvedPop = false
    const isSaving = () => store.getState().saving || (currentOptions.current?.isAdditionalSaving?.() ?? false)
    const needsConfirmation = () => !approvedExit.current && (isSaving() || hasHandheldDrafts(store) || (currentOptions.current?.hasAdditionalDrafts?.() ?? false))
    const request = (action: () => void) => {
      if (needsConfirmation()) setPending(() => action)
      else action()
    }
    const differentScope = (to: Parameters<typeof originalPush>[0]) => {
      const current = currentLocation.current
      const currentUrl = new URL(current.pathname + current.search, window.location.origin)
      const href = typeof to === 'string' ? to : `${to.pathname ?? current.pathname}${to.search ?? ''}`
      const nextUrl = new URL(href, currentUrl)
      const scopeKey = currentOptions.current?.scopeKey
      if (scopeKey) return scopeKey(nextUrl) !== scopeKey(currentUrl)
      const includeOperations = currentOptions.current?.hasAdditionalDrafts?.() ?? false
      return lessonScope(nextUrl, includeOperations) !== lessonScope(currentUrl, includeOperations)
    }
    navigator.push = (...args) => {
      const go = () => { originalPush.apply(navigator, args); index = window.history.state?.idx ?? index }
      if (isSaving() || differentScope(args[0])) request(go)
      else go()
    }
    navigator.replace = (...args) => {
      const go = () => { originalReplace.apply(navigator, args); index = window.history.state?.idx ?? index }
      if (isSaving() || differentScope(args[0])) request(go)
      else go()
    }
    navigator.go = (...args) => {
      // A canonical host can decide POP scope from the actual destination in onPop.
      // Saving still blocks before history moves, including a same-content panel change.
      if (currentOptions.current?.scopeKey && !isSaving()) {
        originalGo.apply(navigator, args)
        return
      }
      request(() => { approvedPop = true; originalGo.apply(navigator, args) })
    }
    const onPop = (event: PopStateEvent) => {
      const nextIndex: unknown = event.state?.idx
      if (restoreAction) {
        event.stopImmediatePropagation()
        const action = restoreAction
        restoreAction = null
        setPending(() => action)
        return
      }
      const scopeKey = currentOptions.current?.scopeKey
      const current = currentLocation.current
      const sameContentPop = scopeKey != null && !isSaving() && scopeKey(new URL(window.location.href))
        === scopeKey(new URL(current.pathname + current.search, window.location.origin))
      if (approvedPop || !needsConfirmation() || sameContentPop) {
        approvedPop = false
        index = typeof nextIndex === 'number' ? nextIndex : index
        return
      }
      if (index != null && typeof nextIndex === 'number' && index !== nextIndex) {
        event.stopImmediatePropagation()
        const delta = index - nextIndex
        restoreAction = () => { approvedPop = true; window.history.go(-delta) }
        window.history.go(delta)
      }
    }
    const onUnload = (event: BeforeUnloadEvent) => {
      if (!needsConfirmation()) return
      event.preventDefault()
      event.returnValue = ''
    }
    const onExitRequest = (event: Event) => {
      if (!(event instanceof CustomEvent)) return
      const action: unknown = event.detail
      if (typeof action !== 'function' || !needsConfirmation()) return
      event.preventDefault()
      request(() => action())
    }
    window.addEventListener(COACHING_EXIT_EVENT, onExitRequest)
    window.addEventListener('popstate', onPop, true)
    window.addEventListener('beforeunload', onUnload)
    return () => {
      navigator.push = originalPush
      navigator.replace = originalReplace
      navigator.go = originalGo
      window.removeEventListener(COACHING_EXIT_EVENT, onExitRequest)
      window.removeEventListener('popstate', onPop, true)
      window.removeEventListener('beforeunload', onUnload)
    }
  }, [navigator, store])

  return <Dialog open={pending != null} onOpenChange={open => { if (!open) setPending(null) }}>
    <DialogContent showCloseButton={false}>
      <DialogHeader><DialogTitle>{t('handheldGrading.leaveTitle')}</DialogTitle>
        <DialogDescription>{t(saving ? 'handheldGrading.waitForSave' : 'handheldGrading.leaveConfirm')}</DialogDescription></DialogHeader>
      <DialogFooter>
        <Button className="min-h-11" variant="outline" onClick={() => setPending(null)}>{t('handheldGrading.stay')}</Button>
        <Button className="min-h-11" variant="destructive" disabled={saving} onClick={() => {
          if (!pending || store.getState().saving || currentOptions.current?.isAdditionalSaving?.()) return
          store.getState().reset()
          currentOptions.current?.discardAdditionalDrafts?.()
          setPending(null)
          // Discard callbacks may queue React state updates; the confirmed exit must still run once.
          approvedExit.current = true
          try {
            pending()
          } finally {
            approvedExit.current = false
          }
        }}>{t('handheldGrading.discardAndLeave')}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
}
